import { useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import {
  UserOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import BlueDivider from "../../../../components/BlueDivider";
import { useCanEdit } from "@/hooks/usePermission";
import sdk from "@/sdk";
import { useEditUser } from "../../modal/useEditUser";

const KYC_SEND_STATUS_COLORS: Record<string, string> = {
  PENDING: "orange",
  COMPLETED: "green",
  EXPIRED: "red",
};
const KYC_SEND_STATUS_LABELS: Record<string, string> = {
  PENDING: "Хүлээгдэж буй",
  COMPLETED: "Бөглөсөн",
  EXPIRED: "Хугацаа дууссан",
};
const KYC_SENDS_PAGE_SIZE = 20;

const BANK_LIST = [
  { name: "Төв Монголбанк", code: "01" },
  { name: "Худалдаа хөгжлийн банк", code: "04" },
  { name: "ХААН Банк", code: "05" },
  { name: "М банк", code: "07" },
  { name: "Голомт банк", code: "15" },
  { name: "Тээвэр хөгжлийн банк", code: "19" },
  { name: "Ариг банк", code: "21" },
  { name: "Кредит банк", code: "22" },
  { name: "Зоос банк", code: "24" },
  { name: "Улаанбаатар хотын банк", code: "26" },
  { name: "Үндэсний хөрөнгө оруулалтын банк", code: "29" },
  { name: "Капитрон банк", code: "30" },
  { name: "Хас банк", code: "32" },
  { name: "Чингис хаан банк", code: "33" },
  { name: "Төрийн банк", code: "34" },
  { name: "Хөгжлийн банк", code: "36" },
  { name: "Богд Банк", code: "38" },
  { name: "МОБИФИНАНС ББСБ", code: "50" },
  { name: "Ард Кредит ББСБ", code: "52" },
] as const;

export const UserTab = ({ _id }: { _id: string }) => {
  const canEdit = useCanEdit("users-list");
  const {
    userData,
    createMSCD,
    isCreatingMSCD,
    userMscdAccount,
    isLoadingMscdAccount,
    updateBankInfo,
    isUpdatingBankInfo,
    userMSCDAccountInternal,
    retryMSCD,
    isRetryingMSCD,
    fixAccountNo,
    isFixingAccountNo,
    updateAccountNo,
    isUpdatingAccountNo,
  } = useEditUser({
    selectedUserId: _id,
  });

  const [editingAccountNo, setEditingAccountNo] = useState(false);
  const [newAccountNo, setNewAccountNo] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [kycSendsPage, setKycSendsPage] = useState(0);
  const { data: kycSendsData, isLoading: isLoadingKycSends } = useQuery({
    queryKey: ["kycQuestionnaireSendsByUser", _id, kycSendsPage],
    queryFn: () =>
      sdk.getKycQuestionnaireSendsByUser({
        userId: _id,
        page: kycSendsPage,
        limit: KYC_SENDS_PAGE_SIZE,
      }),
    enabled: !!_id,
  });
  const kycSends = kycSendsData?.getKycQuestionnaireSendsByUser.sends ?? [];
  const kycSendsTotalPages =
    kycSendsData?.getKycQuestionnaireSendsByUser.totalPages ?? 0;

  const handleOpenModal = () => {
    // Одоо байгаа банкны мэдээллээр bankCode олох
    const currentBank = BANK_LIST.find(
      (b) =>
        b.name === userData?.bankInfo?.bankName ||
        b.code === userData?.bankInfo?.bankCode,
    );
    form.setFieldsValue({
      bankCode: currentBank?.code || "",
      accountNo: userData?.bankInfo?.accountNo || "",
      accountName: userData?.bankInfo?.accountName || "",
    });
    setIsModalOpen(true);
  };

  const handleUpdateBankInfo = () => {
    form.validateFields().then((values) => {
      const bank = BANK_LIST.find((b) => b.code === values.bankCode);
      updateBankInfo({
        _id,
        bankInfo: {
          bankName: bank?.name ?? values.bankCode,
          bankCode: values.bankCode,
          accountNo: values.accountNo,
          accountName: values.accountName,
        },
      });
      setIsModalOpen(false);
    });
  };

  return (
    <>
      <Modal
        title="Дансны мэдээлэл засах"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleUpdateBankInfo}
        okText="Хадгалах"
        cancelText="Цуцлах"
        confirmLoading={isUpdatingBankInfo}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="bankCode"
            label="Банк"
            rules={[{ required: true, message: "Банк сонгоно уу" }]}
          >
            <Select
              placeholder="Банк сонгоно уу"
              showSearch
              optionFilterProp="label"
              options={BANK_LIST.map((b) => ({
                value: b.code,
                label: `${b.name} (${b.code})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="accountNo"
            label="Дансны дугаар"
            rules={[
              { required: true, message: "Дансны дугаар оруулна уу" },
              {
                pattern: /^\d+$/,
                message: "Зөвхөн тоо оруулна уу",
              },
            ]}
          >
            <Input placeholder="Дансны дугаар" />
          </Form.Item>
          <Form.Item name="accountName" label="Дансны нэр">
            <Input placeholder="Дансны нэр" />
          </Form.Item>
        </Form>
      </Modal>

      <Row>
        <Col span={24}>
          <BlueDivider
            text={"Хувийн мэдээлэл"}
            styles={{ marginBottom: "25px", marginTop: "25px" }}
          />
        </Col>
        <Row gutter={24}>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Овог:</div>
              <div>
                {userData?.lastName !== null &&
                userData?.lastName !== undefined &&
                userData.lastName !== "" ? (
                  userData?.lastName
                ) : (
                  <Tag>Мэдээлэл оруулаагүй.</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Нэр:</div>
              <div>
                {userData?.firstName !== null &&
                userData?.firstName !== undefined &&
                userData.firstName !== "" ? (
                  userData?.firstName
                ) : (
                  <Tag>Мэдээлэл оруулаагүй</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Цахим шуудан:</div>
              <div>
                {userData?.email !== null &&
                userData?.email !== undefined &&
                userData.email !== "" ? (
                  userData?.email
                ) : (
                  <Tag>Мэдээлэл оруулаагүй</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Утасны дугаар:</div>
              <div>
                {userData?.phoneNo !== null &&
                userData?.phoneNo !== undefined &&
                userData.phoneNo !== "" ? (
                  "(+976) " + userData?.phoneNo
                ) : (
                  <Tag>Мэдээлэл оруулаагүй</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Хэрэглэгчийн эрх:</div>
              <div>
                {userData?.role !== null && userData?.role !== undefined ? (
                  <Tag>{userData?.role}</Tag>
                ) : (
                  <Tag>Мэдээлэл оруулаагүй.</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Төрсөн огноо:</div>
              <div>
                {userData?.birthDate ? (
                  moment(Number(userData?.birthDate)).format("YYYY/MM/DD")
                ) : (
                  <Tag>Мэдээлэл оруулаагүй.</Tag>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Хэрэглэгчийн төрөл:</div>
              <div>
                {userData?.type !== null && userData?.type !== undefined ? (
                  <Tag>{userData?.type}</Tag>
                ) : (
                  <Tag>Мэдээлэл оруулаагүй.</Tag>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <BlueDivider
            text={"Дансны мэдээлэл"}
            styles={{ marginBottom: "32px" }}
          />
        </Col>
        <Col span={12}>
          <div className="userdetailbox">
            <div>Банкны нэр:</div>
            <div>
              {userData?.bankInfo?.bankName !== null &&
              userData?.bankInfo?.bankName !== undefined &&
              userData.bankInfo?.bankName !== "" ? (
                userData?.bankInfo?.bankName
              ) : (
                <Tag>Мэдээлэл оруулаагүй.</Tag>
              )}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div className="userdetailbox">
            <div>Дансны нэр:</div>
            <div>
              {userData?.bankInfo?.accountName !== null &&
              userData?.bankInfo?.accountName !== undefined &&
              userData.bankInfo?.accountName !== "" ? (
                userData?.bankInfo?.accountName
              ) : (
                <Tag>Мэдээлэл оруулаагүй.</Tag>
              )}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div className="userdetailbox">
            <div>Дансны дугаар:</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {userData?.bankInfo?.accountNo !== null &&
              userData?.bankInfo?.accountNo !== undefined &&
              userData.bankInfo?.accountNo !== "" ? (
                userData?.bankInfo?.accountNo
              ) : (
                <Tag>Мэдээлэл оруулаагүй.</Tag>
              )}
              {canEdit && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleOpenModal}
                />
              )}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div className="userdetailbox">
            <div>ҮЦК дансны дугаар:</div>
            <div>
              {isLoadingMscdAccount ? (
                <Spin size="small" />
              ) : !userMscdAccount ? (
                canEdit ? (
                  <Button
                    icon={<UserOutlined />}
                    className="back-button"
                    loading={isCreatingMSCD}
                    onClick={() => createMSCD(_id)}
                  >
                    Account үүсгэх
                  </Button>
                ) : (
                  <Tag>Байхгүй</Tag>
                )
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {userMscdAccount.bdcAccountId}
                  {canEdit && (
                    <Button
                      icon={<ReloadOutlined />}
                      className="refresh-button"
                      size="small"
                      loading={isCreatingMSCD}
                      onClick={() => createMSCD(_id)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Col>
        {userMscdAccount && (
          <Col span={12}>
            <div className="userdetailbox">
              <div>BDC Account ID:</div>
              <div>{userMscdAccount.bdcAccountId ?? <Tag>Байхгүй</Tag>}</div>
            </div>
          </Col>
        )}
        <Col span={12}>
          <div className="userdetailbox">
            <div>Цэнэглэлт хийх гүйлгээний утга:</div>
            <div>{userData?.depositCode ?? <Tag>Байхгүй байна</Tag>}</div>
          </div>
        </Col>
      </Row>

      {userMSCDAccountInternal && (
        <Row gutter={24}>
          <Col span={24}>
            <BlueDivider
              text="ТТХА данс үүсгэлт"
              styles={{ marginBottom: "16px" }}
            />
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Төлөв:</div>
              <div>
                <Tag
                  color={
                    userMSCDAccountInternal.lastError ? "red" : "processing"
                  }
                >
                  {userMSCDAccountInternal.status}
                </Tag>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Дансны дугаар:</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {canEdit && editingAccountNo ? (
                  <>
                    <Input
                      size="small"
                      style={{ width: 160 }}
                      value={newAccountNo}
                      onChange={(e) => setNewAccountNo(e.target.value)}
                      placeholder="Дансны дугаар"
                    />
                    <Button
                      size="small"
                      loading={isUpdatingAccountNo}
                      onClick={() => {
                        if (!newAccountNo.trim()) return;
                        updateAccountNo({
                          userId: _id,
                          accountNo: newAccountNo.trim(),
                        });
                        setEditingAccountNo(false);
                      }}
                    >
                      Хадгалах
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      loading={isUpdatingAccountNo}
                      onClick={() => {
                        if (!newAccountNo.trim()) return;
                        updateAccountNo({
                          userId: _id,
                          accountNo: newAccountNo.trim(),
                          retry: true,
                        });
                        setEditingAccountNo(false);
                      }}
                    >
                      Хадгалаад дахин илгээх
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setEditingAccountNo(false)}
                    >
                      Болих
                    </Button>
                  </>
                ) : (
                  <>
                    {userMSCDAccountInternal.accountNo}
                    {canEdit && (
                      <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setNewAccountNo(
                            userMSCDAccountInternal.accountNo ?? "",
                          );
                          setEditingAccountNo(true);
                        }}
                      >
                        Засах
                      </Button>
                    )}
                    {canEdit &&
                      (userMSCDAccountInternal.lastError ||
                        userMSCDAccountInternal.mscdStatus === "error") && (
                      <Button
                        size="small"
                        type="link"
                        danger
                        loading={isFixingAccountNo}
                        onClick={() => fixAccountNo(_id)}
                      >
                        Автомат засах
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>MSCD төлөв:</div>
              <div>
                <Tag
                  color={
                    userMSCDAccountInternal.mscdStatus === "created"
                      ? "success"
                      : userMSCDAccountInternal.mscdStatus === "error"
                        ? "red"
                        : userMSCDAccountInternal.mscdStatus === "not_found"
                          ? "default"
                          : "processing"
                  }
                >
                  {userMSCDAccountInternal.mscdStatus === "created"
                    ? "Нээгдсэн"
                    : userMSCDAccountInternal.mscdStatus === "error"
                      ? "Алдаатай"
                      : userMSCDAccountInternal.mscdStatus === "pending"
                        ? "Хүлээгдэж буй"
                        : userMSCDAccountInternal.mscdStatus === "not_found"
                          ? "Олдсонгүй"
                          : "-"}
                </Tag>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Оролдлогын тоо:</div>
              <div>{userMSCDAccountInternal.attemptCount}</div>
            </div>
          </Col>
          <Col span={12}>
            <div className="userdetailbox">
              <div>Сүүлийн оролдлого:</div>
              <div>
                {userMSCDAccountInternal.lastAttemptAt
                  ? moment(userMSCDAccountInternal.lastAttemptAt).format(
                      "YYYY/MM/DD HH:mm",
                    )
                  : "-"}
              </div>
            </div>
          </Col>
          {userMSCDAccountInternal.mscdErrorMessage && (
            <Col span={24}>
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message="MSCD алдаа"
                description={userMSCDAccountInternal.mscdErrorMessage}
                style={{ marginBottom: 8 }}
              />
            </Col>
          )}
          {userMSCDAccountInternal.lastError && (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                icon={<WarningOutlined />}
                message="Алдаа"
                description={userMSCDAccountInternal.lastError}
                style={{ marginBottom: 16 }}
                action={
                  canEdit ? (
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<ReloadOutlined />}
                      loading={isRetryingMSCD}
                      onClick={() => retryMSCD(_id)}
                    >
                      Дахин оролдох
                    </Button>
                  ) : undefined
                }
              />
            </Col>
          )}
        </Row>
      )}

      <Row>
        <Col span={24}>
          <BlueDivider
            text="KYC Судалгаа"
            styles={{ marginBottom: "16px", marginTop: "24px" }}
          />
          <Table
            dataSource={kycSends}
            loading={isLoadingKycSends}
            size="small"
            rowKey="_id"
            bordered
            pagination={{
              current: kycSendsPage + 1,
              total: kycSendsTotalPages * KYC_SENDS_PAGE_SIZE,
              pageSize: KYC_SENDS_PAGE_SIZE,
              onChange: (p) => setKycSendsPage(p - 1),
            }}
            columns={[
              {
                title: "Судалгаа",
                key: "questionnaire",
                render: (_, record) => record.questionnaire?.name ?? "-",
              },
              {
                title: "Төлөв",
                dataIndex: "status",
                key: "status",
                width: 140,
                render: (status: string) => (
                  <Tag color={KYC_SEND_STATUS_COLORS[status] ?? "default"}>
                    {KYC_SEND_STATUS_LABELS[status] ?? status}
                  </Tag>
                ),
              },
              {
                title: "Илгээсэн",
                dataIndex: "sentAt",
                key: "sentAt",
                width: 170,
                render: (val?: number | null) =>
                  val ? moment(val).format("YYYY/MM/DD HH:mm") : "-",
              },
              {
                title: "Бөглөсөн",
                dataIndex: "completedAt",
                key: "completedAt",
                width: 170,
                render: (val?: number | null) =>
                  val ? moment(val).format("YYYY/MM/DD HH:mm") : "-",
              },
              {
                title: "",
                key: "actions",
                width: 60,
                render: (_, record) => (
                  <Link to={`/kyc-questionnaires/send-detail/${record._id}`}>
                    <Button shape="circle" icon={<EyeOutlined />} />
                  </Link>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  );
};
